// src/pages/AdminBilling.js
//
// Lets an eval-plan organizer voluntarily upgrade to a paid tier via
// Stripe Checkout. Reuses the same createCheckoutSession call as
// BecomeOrganizer.js's pre-payment plan picker, but this is a DIFFERENT
// entry point for a DIFFERENT audience: BecomeOrganizer.js's picker only
// shows for an organizer who was manually assigned a paid plan_id
// without having paid (a narrow admin-override edge case). Since every
// new organizer now defaults to plan_id='eval' (with immediate, capped,
// free access -- see evalProvisioning.js), that picker never actually
// appears for a normal new signup. This tab is the real self-serve
// upgrade path: any eval organizer, already fully inside their Manage
// dashboard, can come here and pay to unlock a paid tier's higher limits.

import React, { useState } from 'react';
import { createCheckoutSession } from '../services/api';

const PLANS = [
    { id: 'tier1', name: 'Starter',   price: '$29/yr',  blurb: '4 pools, 50 participants per pool' },
    { id: 'tier2', name: 'Growth',    price: '$49/yr',  blurb: '10 pools, 100 participants per pool' },
    { id: 'tier3', name: 'Unlimited', price: '$100/yr', blurb: 'Unlimited pools, 250 participants per pool' },
];

function AdminBilling({ user = {} }) {
    const [checkingOut, setCheckingOut] = useState(null);
    const [checkoutError, setCheckoutError] = useState('');

    const [checkoutBanner] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const result = params.get('checkout');
        if (result) {
            window.history.replaceState({}, '', window.location.pathname);
        }
        return result;
    });

    const handleCheckout = async (planId) => {
        setCheckingOut(planId);
        setCheckoutError('');
        try {
            const res = await createCheckoutSession(planId);
            window.location.href = res.data.url;
        } catch (err) {
            setCheckoutError(err.response?.data?.error || 'Failed to start checkout');
            setCheckingOut(null);
        }
    };

    // Not on eval -- nothing to upgrade FROM via this tab. (A paid
    // organizer changing tiers, e.g. tier1 -> tier2, isn't built yet;
    // that's a plan-swap/proration question distinct from tonight's gap,
    // which was specifically "eval organizers have no way to pay at all".)
    if (user.plan_id !== 'eval') {
        return (
            <div className="max-w-lg mx-auto text-center py-12">
                <p className="text-gray-400">
                    You're on the <span className="text-orange-400 font-bold">{user.plan_id}</span> plan.
                    Contact a platform admin if you'd like to change tiers.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto py-8">
            {checkoutBanner === 'success' && (
                <div className="mb-6 bg-green-900/30 border border-green-700 rounded-xl p-4 text-center">
                    <p className="text-green-400 text-sm font-bold">Payment received!</p>
                    <p className="text-gray-400 text-xs mt-1">Your plan will activate in just a moment.</p>
                </div>
            )}
            {checkoutBanner === 'cancelled' && (
                <div className="mb-6 bg-gray-800 border border-gray-700 rounded-xl p-4 text-center">
                    <p className="text-gray-300 text-sm">Checkout was cancelled — no charge was made.</p>
                </div>
            )}

            <div className="text-center mb-8">
                <h2 className="text-xl font-black text-white mb-2">Upgrade your plan</h2>
                <p className="text-gray-400 text-sm">
                    You're currently on the free evaluation plan (1 pool, 3 participants). Upgrade
                    to a paid annual plan for higher limits.
                </p>
            </div>

            {checkoutError && <p className="text-red-400 text-sm text-center mb-4">{checkoutError}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {PLANS.map(plan => (
                    <div key={plan.id} className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center flex flex-col">
                        <h3 className="text-lg font-black text-white mb-1">{plan.name}</h3>
                        <p className="text-orange-400 font-bold mb-2">{plan.price}</p>
                        <p className="text-gray-500 text-sm mb-6 flex-grow">{plan.blurb}</p>
                        <button
                            onClick={() => handleCheckout(plan.id)}
                            disabled={checkingOut !== null}
                            className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-4 py-2 rounded-lg transition-colors"
                        >
                            {checkingOut === plan.id ? 'Redirecting…' : 'Upgrade'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AdminBilling;
